import React, { useEffect, useState, useMemo } from 'react';
import DashboardShell from '@/components/DashboardShell';
import api from '@/api/client';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Search, TrendingUp, CreditCard, RefreshCw, PoundSterling } from 'lucide-react';

const METHOD_BADGE = {
  stripe:  { label: 'Stripe',  bg: '#EFF6FF', color: '#1D4ED8' },
  paypal:  { label: 'PayPal',  bg: '#EEF2FF', color: '#4338CA' },
};
const STATUS_BADGE = {
  completed: { label: 'Paid',     bg: '#D1FAE5', color: '#065F46' },
  pending:   { label: 'Pending',  bg: '#FEF3C7', color: '#92400E' },
  refunded:  { label: 'Refunded', bg: '#F3F4F6', color: '#374151' },
  failed:    { label: 'Failed',   bg: '#FEE2E2', color: '#991B1B' },
};

const fmt = (iso) => iso ? new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
const fmtAmt = (amt, cur) => `${cur === 'GBP' ? '£' : cur === 'EUR' ? '€' : '$'}${Number(amt || 0).toFixed(2)}`;

export default function AdminTransactions() {
  const [txs, setTxs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    api.get('/admin/transactions')
      .then(r => setTxs(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => txs.filter(tx => {
    if (statusFilter !== 'all' && tx.status !== statusFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      return tx.listing_title?.toLowerCase().includes(q) || tx.seller_name?.toLowerCase().includes(q) || tx.seller_email?.toLowerCase().includes(q);
    }
    return true;
  }), [txs, search, statusFilter]);

  const totalRevenue = txs.filter(t => t.status === 'completed').reduce((s, t) => s + (t.amount || 0), 0);
  const totalRefunded = txs.filter(t => t.status === 'refunded').reduce((s, t) => s + (t.amount || 0), 0);

  // This month
  const now = new Date();
  const thisMonth = txs.filter(t => {
    if (t.status !== 'completed') return false;
    const d = new Date(t.created_at);
    return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
  }).reduce((s, t) => s + (t.amount || 0), 0);

  const stats = [
    { label: 'Total Revenue', value: `£${totalRevenue.toFixed(2)}`, sub: `${txs.filter(t => t.status === 'completed').length} paid`, icon: TrendingUp, color: '#065F46', bg: '#D1FAE5' },
    { label: 'This Month',    value: `£${thisMonth.toFixed(2)}`,   sub: new Date().toLocaleString('default', { month: 'long', year: 'numeric' }), icon: CreditCard, color: '#1D4ED8', bg: '#EFF6FF' },
    { label: 'Refunded',      value: `£${totalRefunded.toFixed(2)}`, sub: `${txs.filter(t => t.status === 'refunded').length} refund(s)`, icon: RefreshCw, color: '#92400E', bg: '#FEF3C7' },
    { label: 'Transactions',  value: txs.length, sub: 'all time', icon: PoundSterling, color: 'var(--text-muted)', bg: 'var(--bg-subtle)' },
  ];

  return (
    <DashboardShell title="Transactions">
      <div className="space-y-6">
        {/* Summary cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {stats.map(s => (
            <div key={s.label} className="p-5 rounded-xl border" style={{ borderColor: 'var(--border-clr)', backgroundColor: 'var(--surface)' }}>
              <div className="flex items-start justify-between mb-3">
                <p className="text-xs uppercase tracking-widest" style={{ color: 'var(--text-muted)' }}>{s.label}</p>
                <div className="p-1.5 rounded-lg" style={{ backgroundColor: s.bg }}>
                  <s.icon size={13} style={{ color: s.color }} />
                </div>
              </div>
              <p className="text-2xl font-semibold" style={{ fontFamily: 'var(--font-serif)', color: 'var(--text)' }}>{s.value}</p>
              <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>{s.sub}</p>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40" style={{ borderColor: 'var(--border-clr)', backgroundColor: 'var(--surface)' }}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent style={{ backgroundColor: 'var(--surface)', borderColor: 'var(--border-clr)' }}>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="completed">Paid</SelectItem>
              <SelectItem value="refunded">Refunded</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
            </SelectContent>
          </Select>
          <div className="relative flex-1 max-w-xs">
            <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--text-muted)' }} />
            <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search seller or listing…" className="pl-8 text-sm" style={{ borderColor: 'var(--border-clr)', backgroundColor: 'var(--surface)' }} />
          </div>
        </div>

        {/* Table */}
        <div className="rounded-xl border overflow-hidden" style={{ borderColor: 'var(--border-clr)', backgroundColor: 'var(--surface)' }}>
          {loading ? (
            <div className="p-6 space-y-3">{[1,2,3,4].map(i => <div key={i} className="h-12 rounded-lg animate-pulse" style={{ backgroundColor: 'var(--bg-subtle)' }} />)}</div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-16">
              <CreditCard size={32} className="mx-auto mb-3" style={{ color: 'var(--border-strong)' }} />
              <p className="text-sm font-medium" style={{ color: 'var(--text)' }}>No transactions found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="admin-transactions-table">
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--border-clr)', backgroundColor: 'var(--bg-subtle)' }}>
                    {['Date', 'Seller', 'Listing', 'Method', 'Amount', 'Status'].map(h => (
                      <th key={h} className="text-left px-5 py-3 text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((tx, i) => {
                    const method = METHOD_BADGE[tx.payment_method] || { label: tx.payment_method, bg: '#F3F4F6', color: '#374151' };
                    const status = STATUS_BADGE[tx.status] || STATUS_BADGE.pending;
                    return (
                      <tr key={tx.id || i} style={{ borderBottom: '1px solid var(--border-clr)' }} data-testid={`tx-row-${tx.id}`}>
                        <td className="px-5 py-4 whitespace-nowrap" style={{ color: 'var(--text-muted)' }}>{fmt(tx.created_at)}</td>
                        <td className="px-5 py-4">
                          <p className="font-medium" style={{ color: 'var(--text)' }}>{tx.seller_name}</p>
                          <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{tx.seller_email}</p>
                        </td>
                        <td className="px-5 py-4" style={{ color: 'var(--text)', maxWidth: '200px' }}>
                          <p className="truncate">{tx.listing_title}</p>
                        </td>
                        <td className="px-5 py-4">
                          <span className="px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap" style={{ backgroundColor: method.bg, color: method.color }}>{method.label}</span>
                        </td>
                        <td className="px-5 py-4 font-semibold whitespace-nowrap" style={{ color: 'var(--text)' }}>{fmtAmt(tx.amount, tx.currency)}</td>
                        <td className="px-5 py-4">
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap" style={{ backgroundColor: status.bg, color: status.color }}>
                            {tx.status === 'refunded' && <RefreshCw size={10} />}
                            {status.label}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </DashboardShell>
  );
}
